import { useSyncExternalStore } from 'react';
import { tokens } from '@/styles/tokens';

export type Theme = 'dark' | 'light';

// Also read by the inline script in index.html, which applies the theme before first paint.
const STORAGE_KEY = 'theme';
const DEFAULT_THEME: Theme = 'dark';

const listeners = new Set<() => void>();

const readTheme = (): Theme =>
  document.documentElement.dataset.theme === 'light' ? 'light' : DEFAULT_THEME;

const applyTheme = (theme: Theme) => {
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', tokens.themeColor[theme]);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage blocked (private mode): the choice just won't persist.
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Current V2 theme (dark by default) and a setter that persists the user's choice. */
export const useTheme = () => {
  const theme = useSyncExternalStore(subscribe, readTheme);
  return {
    theme,
    setTheme: applyTheme,
    toggleTheme: () => applyTheme(theme === 'dark' ? 'light' : 'dark'),
  };
};
